// src/features/invoice/invoiceActions.js
import {createAsyncThunk} from '@reduxjs/toolkit';
import axios from 'axios';

/* ------------------------------------------------------------------ */
/* Axios helper – reuse one base instance everywhere in the app      */
/* ------------------------------------------------------------------ */
const api = axios.create({
    baseURL: `${process.env.REACT_APP_BASEURL}/user/customer-portal`,
    headers: {'Content-Type': 'application/json'},
});

/* ------------------------------------------------------------------ */
/* 1️⃣ Fetch the current invoice (pricing breakdown)                  */
/*    flags = { includeHardware, includeDrawerboxes, ... }            */
/* ------------------------------------------------------------------ */
export const fetchInvoice = createAsyncThunk(
    'invoice/fetchInvoice',
    async ({orderId, flags}, {rejectWithValue}) => {
        try {
            const {data} = await api.post(`/${orderId}`, flags);
            return data;          // full pricing object
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* ------------------------------------------------------------------ */
/* 2️⃣ Fetch the order-level summary (materials, hardware, etc.)       */
/* ------------------------------------------------------------------ */
export const fetchOrderSummary = createAsyncThunk(
    'invoice/fetchOrderSummary',
    async (orderId, {rejectWithValue}) => {
        try {
            const {data} = await api.post(`/${orderId}/getOrderSummary`);
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* ------------------------------------------------------------------ */
/* 3️⃣ Pay an invoice (deposit / full / custom)                        */
/*    payload shape ➜ { paymentOption, invoiceTxId, invoiceAmount,    */
/*                      salesTax }                                     */
/* ------------------------------------------------------------------ */
export const payInvoice = createAsyncThunk(
    'invoice/payInvoice',
    async ({orderId, payload}, {rejectWithValue}) => {
        try {
            const {data} = await api.post(`/${orderId}/invoicePayment`, payload);
            return data;          // simple “Invoice Payment Success” string
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/* ------------------------------------------------------------------ */
/* 4️⃣ Set finishing options (type + color)                            */
/*    payload shape ➜ { finishingType, finishingColor }               */
/* ------------------------------------------------------------------ */
export const setFinishingOptions = createAsyncThunk(
    'invoice/setFinishingOptions',
    async ({orderId, payload}, {rejectWithValue}) => {
        try {
            const {data} = await api.post(`/${orderId}/setFinishingOptions`, payload);
            return data;          // “Finishing options set”
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);
